import { useEffect, useState } from "react";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import { getAdminStoreSettings, updateAdminStoreSettings } from "@/api/admin.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import {
    DEFAULT_OPENING_HOURS,
    formatOpeningHours,
    isDayBeforeOrEqual,
    WEEK_DAYS
} from "@/utils/openingHours.js";
import {
    EMPTY_SHOP,
    formatAddressSummary,
    shopMapsUrl
} from "@/utils/storeAddress.js";
import ShopPhoneFields from "@/components/ShopPhoneFields.jsx";
import IOSToggle from "@/components/IOSToggle.jsx";
import { buildShopPhonePayload, normalizeShopPhones } from "@/utils/storePhones.js";
import { useStoreInfo } from "@/store/StoreInfoContext.jsx";
import { canManageShopStructure, canWriteLegalSettings, canWriteShops } from "@/utils/staffAccess.js";
import "./opening-hours.css";
import "./website-settings.css";

const EMPTY_LEGAL_DETAILS = {
    legalName: "",
    legalEmail: "",
    legalPhone: "",
    streetLine: "",
    city: "",
    region: "",
    country: ""
};

function createEmptyShopForm() {
    return {
        ...EMPTY_SHOP,
        phones: normalizeShopPhones([]),
        openingHours: { ...DEFAULT_OPENING_HOURS }
    };
}

function normalizeOpeningHours(openingHours) {
    return {
        dayStart: openingHours?.dayStart || DEFAULT_OPENING_HOURS.dayStart,
        dayEnd: openingHours?.dayEnd || DEFAULT_OPENING_HOURS.dayEnd,
        openTime: openingHours?.openTime || DEFAULT_OPENING_HOURS.openTime,
        closeTime: openingHours?.closeTime || DEFAULT_OPENING_HOURS.closeTime,
        comment: openingHours?.comment || ""
    };
}

function validateOpeningHours(openingHours) {
    if (!isDayBeforeOrEqual(openingHours.dayStart, openingHours.dayEnd)) {
        return "Le jour de début doit être avant ou égal au jour de fin.";
    }
    if (openingHours.openTime >= openingHours.closeTime) {
        return "L'heure d'ouverture doit être avant l'heure de fermeture.";
    }
    return "";
}

export default function AdminWebsiteSection({ section = "shops" }) {
    const { role } = useAuth();
    const canWriteLegal = canWriteLegalSettings(role);
    const canWriteShop = canWriteShops(role);
    const canManageStructure = canManageShopStructure(role);
    const { refresh: refreshStoreInfo } = useStoreInfo();
    const [legalDetails, setLegalDetails] = useState(EMPTY_LEGAL_DETAILS);
    const [legalDetailsForm, setLegalDetailsForm] = useState(EMPTY_LEGAL_DETAILS);
    const [legalDetailsEditing, setLegalDetailsEditing] = useState(false);
    const [shops, setShops] = useState([]);
    const [shopForm, setShopForm] = useState(createEmptyShopForm);
    const [editingShopKey, setEditingShopKey] = useState(null);
    const [viewingShopKey, setViewingShopKey] = useState(null);
    const [shopPanelMode, setShopPanelMode] = useState("list");
    const [loading, setLoading] = useState(true);
    const [savingLegalDetails, setSavingLegalDetails] = useState(false);
    const [savingShops, setSavingShops] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const shopKey = (shop, index) => shop.id || `index-${index}`;

    const viewingShop = shops.find((shop, index) => shopKey(shop, index) === viewingShopKey) || null;

    useEffect(() => {
        setLoading(true);
        setMessage("");
        setError("");
        setLegalDetailsEditing(false);
        setEditingShopKey(null);
        setViewingShopKey(null);
        setShopPanelMode("list");
        getAdminStoreSettings()
            .then((response) => {
                const data = response.data || {};
                const nextLegalDetails = {
                    legalName: data.legalName || "",
                    legalEmail: data.legalEmail || "",
                    legalPhone: data.legalPhone || "",
                    streetLine: data.legalAddress?.streetLine || "",
                    city: data.legalAddress?.city || "",
                    region: data.legalAddress?.region || "",
                    country: data.legalAddress?.country || ""
                };
                setLegalDetails(nextLegalDetails);
                setLegalDetailsForm(nextLegalDetails);
                setShops(data.shops || []);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [section]);

    const updateLegalDetailsField = (field, value) => {
        setLegalDetailsForm((prev) => ({ ...prev, [field]: value }));
        setMessage("");
        setError("");
    };

    const backToShopList = () => {
        setShopForm(createEmptyShopForm());
        setEditingShopKey(null);
        setViewingShopKey(null);
        setShopPanelMode("list");
        setError("");
    };

    const openShopView = (shop, index) => {
        setViewingShopKey(shopKey(shop, index));
        setEditingShopKey(null);
        setShopForm(createEmptyShopForm());
        setShopPanelMode("view");
        setMessage("");
        setError("");
    };

    const startAddShop = () => {
        setShopForm(createEmptyShopForm());
        setEditingShopKey("new");
        setViewingShopKey(null);
        setShopPanelMode("create");
        setMessage("");
        setError("");
    };

    const startEditShop = (shop, index) => {
        const key = shopKey(shop, index);
        setShopForm({
            id: shop.id,
            name: shop.name || "",
            streetLine: shop.streetLine || "",
            city: shop.city || "",
            region: shop.region || "",
            country: shop.country || "",
            mapsUrl: shop.mapsUrl || "",
            email: shop.email || "",
            phones: normalizeShopPhones(shop.phones),
            active: shop.active !== false,
            openingHours: normalizeOpeningHours(shop.openingHours)
        });
        setEditingShopKey(key);
        setViewingShopKey(key);
        setShopPanelMode("edit");
        setMessage("");
        setError("");
    };

    const cancelShopForm = () => {
        if (shopPanelMode === "edit" && viewingShopKey) {
            setShopForm(createEmptyShopForm());
            setEditingShopKey(null);
            setShopPanelMode("view");
            setError("");
            return;
        }
        backToShopList();
    };

    const updateShopOpeningHoursField = (field, value) => {
        setShopForm((prev) => ({
            ...prev,
            openingHours: { ...prev.openingHours, [field]: value }
        }));
        setMessage("");
        setError("");
    };

    const applyShopForm = () => {
        const openingHours = normalizeOpeningHours(shopForm.openingHours);
        const hoursError = validateOpeningHours(openingHours);
        if (hoursError) {
            setError(hoursError);
            return;
        }

        const nextShop = {
            id: shopForm.id || null,
            name: shopForm.name.trim(),
            streetLine: shopForm.streetLine.trim(),
            city: shopForm.city.trim(),
            region: shopForm.region.trim(),
            country: shopForm.country.trim(),
            mapsUrl: shopForm.mapsUrl?.trim() || null,
            email: shopForm.email?.trim() || null,
            phones: buildShopPhonePayload(shopForm.phones),
            active: shopForm.active !== false,
            openingHours: {
                ...openingHours,
                comment: openingHours.comment?.trim() || null
            }
        };

        if (!nextShop.name || !nextShop.streetLine || !nextShop.city || !nextShop.country) {
            setError("Le nom, l'adresse, la ville et le pays sont obligatoires pour un magasin.");
            return;
        }

        if (editingShopKey === "new") {
            const nextIndex = shops.length;
            setShops((current) => [...current, nextShop]);
            setViewingShopKey(shopKey(nextShop, nextIndex));
        } else {
            setShops((current) => current.map((shop, index) => {
                const key = shopKey(shop, index);
                return key === editingShopKey ? { ...shop, ...nextShop } : shop;
            }));
            setViewingShopKey(editingShopKey);
        }
        setShopForm(createEmptyShopForm());
        setEditingShopKey(null);
        setShopPanelMode("view");
        setMessage("");
        setError("");
    };

    const removeShop = (shop, index) => {
        const key = shopKey(shop, index);
        setShops((current) => current.filter((item, itemIndex) => shopKey(item, itemIndex) !== key));
        if (viewingShopKey === key || editingShopKey === key) {
            backToShopList();
        }
    };

    const startEditLegalDetails = () => {
        setLegalDetailsForm({ ...legalDetails });
        setLegalDetailsEditing(true);
        setMessage("");
        setError("");
    };

    const cancelEditLegalDetails = () => {
        setLegalDetailsForm({ ...legalDetails });
        setLegalDetailsEditing(false);
        setError("");
    };

    const handleSaveLegalDetails = async (event) => {
        event.preventDefault();
        setMessage("");
        setError("");
        setSavingLegalDetails(true);
        try {
            const response = await updateAdminStoreSettings({
                legalName: legalDetailsForm.legalName.trim(),
                legalEmail: legalDetailsForm.legalEmail.trim(),
                legalPhone: legalDetailsForm.legalPhone.trim(),
                legalAddress: {
                    streetLine: legalDetailsForm.streetLine.trim(),
                    city: legalDetailsForm.city.trim(),
                    region: legalDetailsForm.region.trim() || undefined,
                    country: legalDetailsForm.country.trim()
                }
            });
            const data = response.data || {};
            const nextLegalDetails = {
                legalName: data.legalName || "",
                legalEmail: data.legalEmail || "",
                legalPhone: data.legalPhone || "",
                streetLine: data.legalAddress?.streetLine || "",
                city: data.legalAddress?.city || "",
                region: data.legalAddress?.region || "",
                country: data.legalAddress?.country || ""
            };
            setLegalDetails(nextLegalDetails);
            setLegalDetailsForm(nextLegalDetails);
            setLegalDetailsEditing(false);
            setMessage("Informations légales enregistrées.");
            refreshStoreInfo({ silent: true }).catch(() => {});
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingLegalDetails(false);
        }
    };

    const handleSaveShops = async (event) => {
        event.preventDefault();
        setMessage("");
        setError("");

        if (shops.length === 0) {
            setError("Ajoutez au moins un magasin.");
            return;
        }

        setSavingShops(true);
        try {
            const response = await updateAdminStoreSettings({
                shops: shops.map((shop) => ({
                    id: shop.id || undefined,
                    name: shop.name.trim(),
                    streetLine: shop.streetLine.trim(),
                    city: shop.city.trim(),
                    region: shop.region?.trim() || undefined,
                    country: shop.country.trim(),
                    mapsUrl: shop.mapsUrl?.trim() || undefined,
                    email: shop.email?.trim() || undefined,
                    phones: buildShopPhonePayload(shop.phones),
                    active: shop.active !== false,
                    openingHours: {
                        dayStart: shop.openingHours.dayStart,
                        dayEnd: shop.openingHours.dayEnd,
                        openTime: shop.openingHours.openTime,
                        closeTime: shop.openingHours.closeTime,
                        comment: shop.openingHours.comment?.trim() || null
                    }
                }))
            });
            setShops(response.data?.shops || []);
            backToShopList();
            setMessage("Magasins enregistrés.");
            refreshStoreInfo({ silent: true }).catch(() => {});
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingShops(false);
        }
    };

    return (
        <section className="card profile-section">
            {(section === "legal" || shopPanelMode === "list") && (
                <div className="section-header">
                    <h2>{section === "legal" ? "Informations légales" : "Gérer les magasins"}</h2>
                    {section === "legal" && !legalDetailsEditing && canWriteLegal && (
                        <RowActionsMenu
                            ariaLabel="Actions des informations légales"
                            items={[
                                {
                                    id: "edit",
                                    label: "Modifier",
                                    onClick: startEditLegalDetails
                                }
                            ]}
                        />
                    )}
                </div>
            )}

            {loading && <p className="muted">Chargement des paramètres...</p>}
            {error && <p className="error">{error}</p>}
            {message && <p className="profile-section-feedback profile-section-feedback--success">{message}</p>}

            {!loading && (
                <div className="website-settings">
                    {section === "shops" && (
                    <div className="website-settings-panel">
                        {shopPanelMode === "list" && (
                            <form onSubmit={handleSaveShops}>
                                <p className="muted website-settings-intro">
                                    Adresses et horaires des points de retrait proposés aux clients. Ils sont listés
                                    dans le menu, sur la page contact et proposés lors du retrait en magasin.
                                </p>

                                {shops.length > 0 && (
                                    <ul className="website-shop-list">
                                        {shops.map((shop, index) => (
                                            <li key={shop.id || `shop-${index}`} className="website-shop-item">
                                                <button
                                                    type="button"
                                                    className="website-shop-item-main"
                                                    onClick={() => openShopView(shop, index)}
                                                >
                                                    <div className="website-shop-item-body">
                                                        <strong>{shop.name}</strong>
                                                        <p className="muted">{formatAddressSummary(shop)}</p>
                                                        {shop.openingHours && (
                                                            <p className="muted">{formatOpeningHours(shop.openingHours)}</p>
                                                        )}
                                                        {shop.email && <p className="muted">{shop.email}</p>}
                                                        {shop.phones?.length > 0 && (
                                                            <p className="muted">
                                                                {shop.phones.map((phone) => phone.phoneNumber).join(", ")}
                                                            </p>
                                                        )}
                                                        {shop.active === false && <span className="muted">Inactif</span>}
                                                    </div>
                                                    <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                                                </button>
                                                <RowActionsMenu
                                                    ariaLabel={`Actions pour ${shop.name}`}
                                                    items={[
                                                        {
                                                            id: "view",
                                                            label: "Voir les détails",
                                                            onClick: () => openShopView(shop, index)
                                                        },
                                                        ...(canWriteShop
                                                            ? [{
                                                                id: "edit",
                                                                label: "Modifier",
                                                                onClick: () => startEditShop(shop, index)
                                                            }]
                                                            : []),
                                                        ...(canManageStructure
                                                            ? [{
                                                                id: "remove",
                                                                label: "Retirer",
                                                                danger: true,
                                                                onClick: () => removeShop(shop, index)
                                                            }]
                                                            : [])
                                                    ]}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {canManageStructure && (
                                    <button type="button" className="btn btn-secondary btn-inline" onClick={startAddShop}>
                                        Ajouter un magasin
                                    </button>
                                )}

                                {canWriteShop && (
                                    <div className="profile-form-actions">
                                        <button className="btn btn-primary btn-inline" type="submit" disabled={savingShops}>
                                            {savingShops ? "Enregistrement..." : "Enregistrer les magasins"}
                                        </button>
                                    </div>
                                )}
                            </form>
                        )}

                        {shopPanelMode === "view" && viewingShop && (
                            <>
                                <div className="section-header">
                                    <div>
                                        <BackLink onClick={backToShopList} className="purchase-detail-back">
                                            Retour à la liste
                                        </BackLink>
                                        <h3>Détails du magasin</h3>
                                    </div>
                                    <RowActionsMenu
                                        ariaLabel={`Actions pour ${viewingShop.name}`}
                                        items={[
                                            ...(canWriteShop
                                                ? [{
                                                    id: "edit",
                                                    label: "Modifier",
                                                    onClick: () => {
                                                        const index = shops.findIndex(
                                                            (shop, shopIndex) => shopKey(shop, shopIndex) === viewingShopKey
                                                        );
                                                        if (index >= 0) {
                                                            startEditShop(shops[index], index);
                                                        }
                                                    }
                                                }]
                                                : []),
                                            ...(canManageStructure
                                                ? [{
                                                    id: "remove",
                                                    label: "Retirer",
                                                    danger: true,
                                                    onClick: () => {
                                                        const index = shops.findIndex(
                                                            (shop, shopIndex) => shopKey(shop, shopIndex) === viewingShopKey
                                                        );
                                                        if (index >= 0) {
                                                            removeShop(shops[index], index);
                                                        }
                                                    }
                                                }]
                                                : [])
                                        ]}
                                    />
                                </div>

                                <dl className="profile-detail-view profile-detail-view--compact">
                                    <div className="profile-detail-row">
                                        <dt>Nom</dt>
                                        <dd>{viewingShop.name || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Adresse</dt>
                                        <dd>{formatAddressSummary(viewingShop) || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>E-mail</dt>
                                        <dd>{viewingShop.email || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Téléphones</dt>
                                        <dd>
                                            {viewingShop.phones?.length ? (
                                                <ul className="profile-phone-view-list">
                                                    {viewingShop.phones.map((phone, phoneIndex) => (
                                                        <li key={`${phone.id || phone.phoneNumber}-${phoneIndex}`}>
                                                            {phone.phoneNumber}
                                                            {phone.label ? ` (${phone.label})` : ""}
                                                            {phone.whatsappEnabled ? " · WhatsApp" : ""}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                "—"
                                            )}
                                        </dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Horaires</dt>
                                        <dd>
                                            {viewingShop.openingHours
                                                ? formatOpeningHours(viewingShop.openingHours)
                                                : "—"}
                                        </dd>
                                    </div>
                                    {viewingShop.openingHours?.comment && (
                                        <div className="profile-detail-row">
                                            <dt>Commentaire horaires</dt>
                                            <dd>{viewingShop.openingHours.comment}</dd>
                                        </div>
                                    )}
                                    <div className="profile-detail-row">
                                        <dt>Google Maps</dt>
                                        <dd>
                                            {shopMapsUrl(viewingShop) ? (
                                                <a href={shopMapsUrl(viewingShop)} target="_blank" rel="noreferrer">
                                                    Ouvrir la carte
                                                </a>
                                            ) : (
                                                "—"
                                            )}
                                        </dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Statut</dt>
                                        <dd>{viewingShop.active === false ? "Inactif" : "Actif pour le retrait"}</dd>
                                    </div>
                                </dl>
                            </>
                        )}

                        {(shopPanelMode === "create" || shopPanelMode === "edit") && (
                            <div className="website-shop-form card">
                                <div className="section-header">
                                    <div>
                                        <BackLink onClick={cancelShopForm} className="purchase-detail-back">
                                            {shopPanelMode === "edit" ? "Retour au détail" : "Retour à la liste"}
                                        </BackLink>
                                        <h4>{shopPanelMode === "create" ? "Ajouter un magasin" : "Modifier le magasin"}</h4>
                                    </div>
                                </div>
                                <label className="field">
                                    Nom du point de vente
                                    <input
                                        value={shopForm.name}
                                        onChange={(event) => setShopForm({ ...shopForm, name: event.target.value })}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Adresse
                                    <input
                                        value={shopForm.streetLine}
                                        onChange={(event) => setShopForm({ ...shopForm, streetLine: event.target.value })}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Ville
                                    <input
                                        value={shopForm.city}
                                        onChange={(event) => setShopForm({ ...shopForm, city: event.target.value })}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Région (optionnel)
                                    <input
                                        value={shopForm.region}
                                        onChange={(event) => setShopForm({ ...shopForm, region: event.target.value })}
                                    />
                                </label>
                                <label className="field">
                                    Pays
                                    <input
                                        value={shopForm.country}
                                        onChange={(event) => setShopForm({ ...shopForm, country: event.target.value })}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Lien Google Maps (optionnel)
                                    <input
                                        type="url"
                                        value={shopForm.mapsUrl}
                                        onChange={(event) => setShopForm({ ...shopForm, mapsUrl: event.target.value })}
                                        placeholder="https://www.google.com/maps/place/..."
                                    />
                                </label>
                                <label className="field">
                                    E-mail (optionnel)
                                    <input
                                        type="email"
                                        value={shopForm.email}
                                        onChange={(event) => setShopForm({ ...shopForm, email: event.target.value })}
                                        placeholder="contact@magasin.cm"
                                    />
                                </label>
                                <ShopPhoneFields
                                    phones={shopForm.phones}
                                    onChange={(phones) => setShopForm({ ...shopForm, phones })}
                                    idPrefix="shop-form-phone"
                                />
                                <IOSToggle
                                    id="shop-form-active"
                                    label="Magasin actif pour le retrait"
                                    checked={shopForm.active !== false}
                                    onChange={(active) => setShopForm({ ...shopForm, active })}
                                />

                                <div className="website-shop-hours">
                                    <h5>Horaires d&apos;ouverture</h5>
                                    <div className="opening-hours-calendar">
                                        <div className="opening-hours-field">
                                            <label htmlFor="shopDayStart">Du</label>
                                            <select
                                                id="shopDayStart"
                                                value={shopForm.openingHours.dayStart}
                                                onChange={(event) => updateShopOpeningHoursField("dayStart", event.target.value)}
                                            >
                                                {WEEK_DAYS.map((day) => (
                                                    <option key={day.value} value={day.value}>{day.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="opening-hours-field">
                                            <label htmlFor="shopDayEnd">Au</label>
                                            <select
                                                id="shopDayEnd"
                                                value={shopForm.openingHours.dayEnd}
                                                onChange={(event) => updateShopOpeningHoursField("dayEnd", event.target.value)}
                                            >
                                                {WEEK_DAYS.map((day) => (
                                                    <option key={day.value} value={day.value}>{day.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="opening-hours-field">
                                            <label htmlFor="shopOpenTime">De</label>
                                            <input
                                                id="shopOpenTime"
                                                type="time"
                                                value={shopForm.openingHours.openTime}
                                                onChange={(event) => updateShopOpeningHoursField("openTime", event.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="opening-hours-field">
                                            <label htmlFor="shopCloseTime">À</label>
                                            <input
                                                id="shopCloseTime"
                                                type="time"
                                                value={shopForm.openingHours.closeTime}
                                                onChange={(event) => updateShopOpeningHoursField("closeTime", event.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="opening-hours-preview">
                                        <span className="contact-label">Aperçu</span>
                                        <p>{formatOpeningHours(shopForm.openingHours)}</p>
                                    </div>

                                    <div className="opening-hours-field opening-hours-comment">
                                        <label htmlFor="shopHoursComment">Commentaire</label>
                                        <textarea
                                            id="shopHoursComment"
                                            rows={3}
                                            maxLength={1000}
                                            value={shopForm.openingHours.comment}
                                            onChange={(event) => updateShopOpeningHoursField("comment", event.target.value)}
                                            placeholder="Ex. : fermeture exceptionnelle les jours fériés..."
                                        />
                                    </div>
                                </div>

                                <div className="profile-form-actions">
                                    <button type="button" className="btn btn-primary btn-inline" onClick={applyShopForm}>
                                        {shopPanelMode === "create" ? "Ajouter à la liste" : "Appliquer"}
                                    </button>
                                    <button type="button" className="btn btn-secondary btn-inline" onClick={cancelShopForm}>
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {section === "legal" && (
                    <div className="website-settings-panel">
                        <p className="muted website-settings-intro">
                            Raison sociale, coordonnées et adresse de l&apos;entité éditrice, affichées dans le
                            pied de page et les mentions légales.
                        </p>

                        {!legalDetailsEditing ? (
                            <>
                                <dl className="profile-detail-view profile-detail-view--compact">
                                    <div className="profile-detail-row">
                                        <dt>Raison sociale</dt>
                                        <dd>{legalDetails.legalName || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>E-mail</dt>
                                        <dd>{legalDetails.legalEmail || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Téléphone</dt>
                                        <dd>{legalDetails.legalPhone || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Adresse</dt>
                                        <dd>{legalDetails.streetLine || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Ville</dt>
                                        <dd>{legalDetails.city || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Région</dt>
                                        <dd>{legalDetails.region || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Pays</dt>
                                        <dd>{legalDetails.country || "—"}</dd>
                                    </div>
                                </dl>
                            </>
                        ) : (
                            <form onSubmit={handleSaveLegalDetails}>
                                <label className="field">
                                    Raison sociale
                                    <input
                                        value={legalDetailsForm.legalName}
                                        onChange={(event) => updateLegalDetailsField("legalName", event.target.value)}
                                        maxLength={255}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    E-mail
                                    <input
                                        type="email"
                                        value={legalDetailsForm.legalEmail}
                                        onChange={(event) => updateLegalDetailsField("legalEmail", event.target.value)}
                                        maxLength={255}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Téléphone
                                    <input
                                        type="tel"
                                        value={legalDetailsForm.legalPhone}
                                        onChange={(event) => updateLegalDetailsField("legalPhone", event.target.value)}
                                        maxLength={50}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Adresse
                                    <input
                                        value={legalDetailsForm.streetLine}
                                        onChange={(event) => updateLegalDetailsField("streetLine", event.target.value)}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Ville
                                    <input
                                        value={legalDetailsForm.city}
                                        onChange={(event) => updateLegalDetailsField("city", event.target.value)}
                                        required
                                    />
                                </label>
                                <label className="field">
                                    Région (optionnel)
                                    <input
                                        value={legalDetailsForm.region}
                                        onChange={(event) => updateLegalDetailsField("region", event.target.value)}
                                    />
                                </label>
                                <label className="field">
                                    Pays
                                    <input
                                        value={legalDetailsForm.country}
                                        onChange={(event) => updateLegalDetailsField("country", event.target.value)}
                                        required
                                    />
                                </label>

                                <div className="profile-form-actions">
                                    <button className="btn btn-primary btn-inline" type="submit" disabled={savingLegalDetails}>
                                        {savingLegalDetails ? "Enregistrement..." : "Enregistrer"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-inline"
                                        onClick={cancelEditLegalDetails}
                                        disabled={savingLegalDetails}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                    )}
                </div>
            )}
        </section>
    );
}
